import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft,
  Loader2,
  Briefcase,
  MapPin,
  Building2,
  DollarSign,
  Calendar,
  ExternalLink,
  Sparkles,
  Target,
  TrendingUp,
  CheckCircle,
  Clock,
  RefreshCw,
  ChevronRight,
  Award,
  Zap,
  Info,
  HelpCircle,
  BookOpen,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

// Job Matches Help Modal Component
const JobMatchesHelpModal = ({ isOpen, onClose, onNavigateToHelp }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            Job Matches
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this page?</h4>
            <p className="text-sm text-slate-600">
              This page shows your AI-curated job matches. Unlike a traditional job board, we only show roles that meaningfully align with your skills, experience, and career goals.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is V-FIT?</h4>
            <p className="text-sm text-slate-600">
              V-FIT (Varyynt Future-Intent Trajectory) is our proprietary scoring algorithm. It analyzes skills, function, seniority, industry, company, compensation, and location to give each job a 0-100 match score.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Match Strengths</h4>
            <ul className="space-y-2">
              {[
                { label: 'Strong Match (80+)', desc: 'Excellent fit — prioritize these' },
                { label: 'Clear Match (60-79)', desc: 'Good alignment — worth exploring' },
                { label: 'Emerging Match (40-59)', desc: 'Potential opportunity — review carefully' }
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  <span><strong>{item.label}:</strong> {item.desc}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Match strength configurations
const STRENGTH_CONFIG = {
  strong: {
    label: 'Strong Match',
    color: 'bg-lime-100 text-lime-700 border-lime-200',
    icon: Award,
    description: 'Excellent fit based on your skills and preferences',
    gradient: 'from-lime-500/10 to-emerald-500/10',
    border: 'border-lime-300',
  },
  clear: {
    label: 'Clear Match',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Target,
    description: 'Good alignment with your career goals',
    gradient: 'from-blue-500/10 to-indigo-500/10',
    border: 'border-blue-300',
  },
  emerging: {
    label: 'Emerging Match',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: TrendingUp,
    description: 'Potential opportunity worth exploring',
    gradient: 'from-amber-500/10 to-orange-500/10',
    border: 'border-amber-300',
  },
};

// V-FIT Score Badge Component
const VFITScoreBadge = ({ score, size = 'default' }) => {
  const getColor = () => {
    if (score >= 80) return 'bg-lime-500 text-white';
    if (score >= 60) return 'bg-blue-500 text-white';
    if (score >= 40) return 'bg-amber-500 text-white';
    return 'bg-slate-400 text-white';
  };

  const sizeClasses = size === 'large' 
    ? 'w-16 h-16 text-xl' 
    : 'w-10 h-10 text-sm';

  return (
    <div className={`${sizeClasses} ${getColor()} rounded-full flex items-center justify-center font-bold shadow-lg`}>
      {Math.round(score)}
    </div>
  );
};

// Match Card Component
const MatchCard = ({ match, onView }) => {
  const strength = STRENGTH_CONFIG[match.match_strength] || STRENGTH_CONFIG.emerging;
  const StrengthIcon = strength.icon;
  const job = match.job;

  const formatSalary = () => {
    if (!job.salary_min && !job.salary_max) return null;
    const currency = job.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency;
    
    if (job.salary_min && job.salary_max) {
      return `${symbol}${(job.salary_min / 1000).toFixed(0)}K - ${symbol}${(job.salary_max / 1000).toFixed(0)}K`;
    }
    if (job.salary_max) {
      return `Up to ${symbol}${(job.salary_max / 1000).toFixed(0)}K`;
    }
    return `${symbol}${(job.salary_min / 1000).toFixed(0)}K+`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className={`bg-gradient-to-br ${strength.gradient} rounded-xl border ${strength.border} p-5 hover:shadow-lg transition-all cursor-pointer group`}
      onClick={() => onView(match)}
      data-testid={`match-card-${match.job_id}`}
    >
      <div className="flex gap-4">
        {/* Company Logo / Score */}
        <div className="flex flex-col items-center gap-2">
          {job.company_logo_url ? (
            <img 
              src={job.company_logo_url} 
              alt={job.company_name}
              className="w-14 h-14 rounded-lg object-contain bg-white p-1 border"
            />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-white border flex items-center justify-center">
              <Building2 className="h-6 w-6 text-slate-400" />
            </div>
          )}
          <VFITScoreBadge score={match.vfit_score} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <Badge className={`${strength.color} mb-2`}>
                <StrengthIcon className="h-3 w-3 mr-1" />
                {strength.label}
              </Badge>
              <h3 className="font-semibold text-slate-900 text-lg group-hover:text-lime-700 transition-colors">
                {job.title}
              </h3>
              <p className="text-slate-600">{job.company_name}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-lime-600 transition-colors" />
          </div>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-3 text-sm text-slate-500 mb-3">
            {job.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {job.location}
              </span>
            )}
            {job.location_type && (
              <Badge variant="outline" className="capitalize text-xs">
                {job.location_type}
              </Badge>
            )}
            {formatSalary() && (
              <span className="flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" />
                {formatSalary()}
              </span>
            )}
            {job.date_posted && (
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {formatDistanceToNow(new Date(job.date_posted), { addSuffix: true })}
              </span>
            )}
          </div>

          {/* Match Reasons Preview */}
          {match.reasons && match.reasons.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {match.reasons.slice(0, 2).map((reason, idx) => (
                <span 
                  key={idx}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white/60 rounded-full text-slate-600"
                >
                  <CheckCircle className="h-3 w-3 text-lime-500" />
                  {reason}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Job Detail Modal
const JobDetailModal = ({ match, isOpen, onClose, onApply }) => {
  if (!match) return null;

  const strength = STRENGTH_CONFIG[match.match_strength] || STRENGTH_CONFIG.emerging;
  const job = match.job;

  const formatSalary = () => {
    if (!job.salary_min && !job.salary_max) return 'Not specified';
    const currency = job.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency;
    
    if (job.salary_min && job.salary_max) {
      return `${symbol}${(job.salary_min).toLocaleString()} - ${symbol}${(job.salary_max).toLocaleString()}`;
    }
    if (job.salary_max) {
      return `Up to ${symbol}${(job.salary_max).toLocaleString()}`;
    }
    return `${symbol}${(job.salary_min).toLocaleString()}+`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-4">
            {job.company_logo_url ? (
              <img 
                src={job.company_logo_url} 
                alt={job.company_name}
                className="w-16 h-16 rounded-lg object-contain bg-white p-1 border"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                <Building2 className="h-8 w-8 text-slate-400" />
              </div>
            )}
            <div className="flex-1">
              <DialogTitle className="text-xl mb-1">{job.title}</DialogTitle>
              <p className="text-slate-600">{job.company_name}</p>
              {job.company_url && (
                <a 
                  href={job.company_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-lime-600 hover:underline"
                >
                  Visit company website
                </a>
              )}
            </div>
            <VFITScoreBadge score={match.vfit_score} size="large" />
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* V-FIT Match Summary */}
          <div className={`p-4 rounded-xl bg-gradient-to-r ${strength.gradient} border ${strength.border}`}>
            <div className="flex items-center gap-2 mb-3">
              <Badge className={strength.color}>
                <Sparkles className="h-3 w-3 mr-1" />
                V-FIT Score: {Math.round(match.vfit_score)}/100
              </Badge>
              <Badge className={strength.color}>{strength.label}</Badge>
            </div>
            
            {match.reasons && match.reasons.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Why this matches you:</p>
                <ul className="space-y-1">
                  {match.reasons.map((reason, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-lime-500 flex-shrink-0" />
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Job Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Location</p>
              <p className="font-medium text-slate-900">
                {job.location || 'Not specified'}
                {job.location_type && (
                  <Badge variant="outline" className="ml-2 capitalize">
                    {job.location_type}
                  </Badge>
                )}
              </p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Salary</p>
              <p className="font-medium text-slate-900">{formatSalary()}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Job Type</p>
              <p className="font-medium text-slate-900 capitalize">{job.job_type || 'Full-time'}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Posted</p>
              <p className="font-medium text-slate-900">
                {job.date_posted ? format(new Date(job.date_posted), 'MMM d, yyyy') : 'Recently'}
              </p>
            </div>
            {job.industry && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Industry</p>
                <p className="font-medium text-slate-900 capitalize">{job.industry}</p>
              </div>
            )}
            {job.company_size && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Company Size</p>
                <p className="font-medium text-slate-900">{job.company_size} employees</p>
              </div>
            )}
          </div>

          {/* Description */}
          {job.description && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">About the Role</h3>
              <div className="prose prose-sm max-w-none text-slate-600">
                <p className="whitespace-pre-wrap">{job.description}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              className="flex-1 bg-evergreen-950 hover:bg-evergreen-900"
              onClick={() => onApply(job)}
              data-testid="apply-now-btn"
            >
              Apply Now
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Empty State Component
const EmptyState = ({ onRefresh, isLoading }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="text-center py-16"
  >
    <div className="max-w-md mx-auto">
      {/* AI Agent Illustration */}
      <div className="relative mb-8">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-32 rounded-full bg-lime-100 animate-pulse" />
        </div>
        <div className="relative flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-evergreen-950 to-evergreen-900 flex items-center justify-center shadow-xl">
            <Sparkles className="h-10 w-10 text-lime-400" />
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-heading text-slate-900 mb-3">
        Your AI Agent is Working
      </h2>
      <p className="text-slate-500 mb-6 max-w-sm mx-auto">
        Strong matches will appear here when we find roles that align with your profile. This isn&apos;t a job board — it&apos;s your personal career agent filtering out the noise.
      </p>

      <div className="space-y-4">
        <Button
          onClick={onRefresh}
          disabled={isLoading}
          className="bg-evergreen-950 hover:bg-evergreen-900"
          data-testid="sync-profile-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Syncing Profile...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync My Profile
            </>
          )}
        </Button>

        <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
          <Clock className="h-4 w-4" />
          <span>Matches update as new roles appear</span>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-8 p-4 bg-slate-50 rounded-xl text-left">
        <h3 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
          <Zap className="h-4 w-4 text-lime-600" />
          While you wait...
        </h3>
        <ul className="text-sm text-slate-600 space-y-2">
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
            Make sure your profile is 100% complete
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
            Check your dealbreakers are set correctly
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
            Upload your latest resume for better matching
          </li>
        </ul>
      </div>
    </div>
  </motion.div>
);

export default function JobMatchesPage() {
  const { authAxios, logout, user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'strong', 'clear'
  const [showHelp, setShowHelp] = useState(false);

  const loadMatches = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.strength = filter;
      }
      const response = await authAxios.get(`/jobs/matches?${params}`);
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to load matches:', error);
      toast.error('Failed to load job matches');
    } finally {
      setLoading(false);
    }
  }, [authAxios, filter]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await authAxios.post('/jobs/sync-profile');
      toast.success('Profile synced! Refreshing matches...');
      await loadMatches();
    } catch (error) {
      console.error('Failed to sync:', error);
      toast.error('Failed to sync profile');
    } finally {
      setSyncing(false);
    }
  };

  const handleView = (match) => {
    setSelectedMatch(match);
    setDetailOpen(true);
  };

  const handleApply = (job) => {
    if (job.apply_url) {
      window.open(job.apply_url, '_blank');
    } else if (job.apply_email) {
      window.location.href = `mailto:${job.apply_email}?subject=Application for ${job.title}`;
    } else {
      toast.error('No application method available');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-lime-600 mx-auto mb-4" />
          <p className="text-slate-500">Finding your best matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>
              Sign Out
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-slate-500 hover:text-lime-600"
              onClick={() => setShowHelp(true)}
              data-testid="matches-help-btn"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading text-slate-900 mb-1">Your Job Matches</h1>
              <p className="text-slate-500">
                {matches.length > 0 
                  ? `${matches.length} active ${matches.length === 1 ? 'opportunity' : 'opportunities'} matched to your profile`
                  : 'Opportunities matching your profile will appear here'
                }
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={syncing}
              data-testid="header-sync-btn"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2 hidden sm:inline">Refresh</span>
            </Button>
          </div>

          {/* Filter Tabs */}
          {matches.length > 0 && (
            <div className="flex gap-2 mt-4">
              {[
                { value: 'all', label: 'All Matches' },
                { value: 'strong', label: 'Strong', icon: Award },
                { value: 'clear', label: 'Clear', icon: Target },
              ].map((tab) => (
                <Button
                  key={tab.value}
                  variant={filter === tab.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(tab.value)}
                  className={filter === tab.value ? 'bg-evergreen-950' : ''}
                  data-testid={`filter-${tab.value}`}
                >
                  {tab.icon && <tab.icon className="h-3.5 w-3.5 mr-1" />}
                  {tab.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        {matches.length === 0 ? (
          <EmptyState onRefresh={handleSync} isLoading={syncing} />
        ) : (
          <AnimatePresence mode="popLayout">
            <div className="space-y-4">
              {matches.map((match) => (
                <MatchCard
                  key={match.job_id}
                  match={match}
                  onView={handleView}
                />
              ))}
            </div>
          </AnimatePresence>
        )}

        {/* Info Card */}
        <Card className="mt-8 border-lime-200 bg-lime-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-lime-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">About V-FIT Matching</p>
                <p className="text-sm text-slate-600 mt-1">
                  Your V-FIT (Varyynt Future-Intent Trajectory) score measures how well a role aligns with your skills, experience, and career aspirations. 
                  We only show you matches scoring 40+ to focus your time on real opportunities.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Detail Modal */}
      <JobDetailModal
        match={selectedMatch}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onApply={handleApply}
      />

      {/* Job Matches Help Modal */}
      <JobMatchesHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onNavigateToHelp={() => {
          setShowHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
